/**
 * MapStyleService - Servicio para gestión de estilos de mapa
 *
 * @version 2.0
 * @updated 2026-02-10
 * @module core/map-engine
 *
 * @description
 * Servicio singleton que centraliza la configuración de estilos de mapa.
 * Gestiona API keys de MapTiler y proporciona fallback automático.
 *
 * @remarks
 * **Changelog v2.0**:
 * - Expandido de 3 a 5 estilos (dark, light, streets, terrain, satellite)
 * - Refactorizado para usar map-styles-config.ts (modularidad)
 * - Agregado getAvailableStyles() para listar estilos disponibles
 * - Agregado getStyleMetadata() para acceder a metadata
 * - Mejorado manejo de fallbacks cuando no hay API key
 *
 * @example
 * ```typescript
 * import { mapStyleService } from '@/core/map-engine';
 *
 * // Obtener estilo default
 * const style = mapStyleService.getDefaultStyle();
 *
 * // Cambiar estilo
 * const satelliteStyle = mapStyleService.getStyle('satellite');
 *
 * // Listar estilos disponibles
 * const available = mapStyleService.getAvailableStyles();
 * ```
 */

import type { MapStyleType, MapStyleMetadata } from './map-styles-config';
import { MAP_STYLES_CONFIG, getAllStyles, getStyleMetadata } from './map-styles-config';
import { solidColorService } from './solid-color-service';

/**
 * Servicio singleton para gestión de estilos de mapa
 *
 * @remarks
 * Este servicio proporciona una interfaz centralizada para acceder a estilos
 * de MapTiler con fallback automático cuando no hay API key configurada.
 *
 * **Arquitectura modular**:
 * - Configuración de estilos: map-styles-config.ts (fácil de ajustar)
 * - Lógica de negocio: MapStyleService.ts (estable)
 * - UI de selección: MapStyleSelector.tsx (componente React)
 */
export class MapStyleService {
  private static instance: MapStyleService;
  private readonly apiKey: string;
  private currentStyle: MapStyleType = 'dark';

  /**
   * Constructor privado (patrón Singleton)
   */
  private constructor() {
    // Obtener API key de variables de entorno de Vite
    this.apiKey = import.meta.env.VITE_MAPTILER_API_KEY || '';

    // Advertir si no hay API key configurada
    if (!this.hasApiKey()) {
      console.warn(
        '[MapStyleService] MapTiler API key no configurada.\n' +
          'Algunos estilos no estarán disponibles (light, terrain, satellite).\n' +
          'Para obtener acceso completo:\n' +
          '1. Obtén una API key gratuita en https://cloud.maptiler.com/\n' +
          '2. Agrégala a .env como VITE_MAPTILER_API_KEY=tu_key\n' +
          '3. Reinicia el servidor (pnpm dev)'
      );
    }
  }

  /**
   * Obtiene la instancia única del servicio
   *
   * @returns Instancia singleton de MapStyleService
   */
  public static getInstance(): MapStyleService {
    if (!MapStyleService.instance) {
      MapStyleService.instance = new MapStyleService();
    }
    return MapStyleService.instance;
  }

  /**
   * Verifica si hay una API key configurada
   *
   * @returns true si hay API key, false en caso contrario
   */
  public hasApiKey(): boolean {
    return this.apiKey.length > 0;
  }

  /**
   * Obtiene el estilo actualmente seleccionado
   *
   * @returns Tipo de estilo actual
   */
  public getCurrentStyle(): MapStyleType {
    return this.currentStyle;
  }

  /**
   * Establece el estilo actual
   *
   * @param type - Tipo de estilo a establecer
   */
  public setCurrentStyle(type: MapStyleType): void {
    this.currentStyle = type;
  }

  /**
   * Obtiene el estilo por defecto (dark)
   *
   * @remarks
   * Si no hay API key configurada, retorna el estilo demo de MapLibre
   * y muestra un warning en consola.
   *
   * @returns URL del estilo de mapa
   */
  public getDefaultStyle(): string {
    return this.getStyle('dark');
  }

  /**
   * Obtiene un estilo específico
   *
   * @param type - Tipo de estilo ('dark', 'light', 'streets', 'terrain', 'satellite', 'solid')
   * @returns URL del estilo de mapa o objeto de estilo inline
   *
   * @remarks
   * Si no hay API key y el estilo no tiene fallback, retorna el estilo dark
   * con fallback para evitar errores.
   *
   * Para el estilo 'solid', genera un estilo inline con el color configurado.
   *
   * @example
   * ```typescript
   * const darkStyle = mapStyleService.getStyle('dark');
   * const satelliteStyle = mapStyleService.getStyle('satellite');
   * const solidStyle = mapStyleService.getStyle('solid'); // Retorna objeto inline
   * ```
   */
  public getStyle(type: MapStyleType): string | object {
    const metadata = MAP_STYLES_CONFIG[type];

    if (!metadata) {
      console.error(`[MapStyleService] Estilo desconocido: ${type}. Usando 'dark'.`);
      return this.getStyle('dark');
    }

    // Caso especial: estilo solid (fondo liso con color configurable)
    if (type === 'solid') {
      return this.getSolidColorStyle();
    }

    // Si hay API key, usar MapTiler
    if (this.hasApiKey()) {
      const baseUrl = 'https://api.maptiler.com/maps';
      return `${baseUrl}/${metadata.maptilerStyle}/style.json?key=${this.apiKey}`;
    }

    // Sin API key: usar fallback si está disponible
    if (metadata.fallbackUrl && metadata.fallbackUrl !== 'inline') {
      return metadata.fallbackUrl;
    }

    // Sin API key y sin fallback: usar estilo dark con fallback
    console.warn(
      `[MapStyleService] Estilo '${type}' requiere API key. Usando fallback 'dark'.`
    );
    return MAP_STYLES_CONFIG.dark.fallbackUrl!;
  }

  /**
   * Obtiene la URL de tiles raster XYZ de MapTiler para un estilo
   *
   * @param type - Tipo de estilo
   * @returns Plantilla de URL `{z}/{x}/{y}` con la key ya incluida, o `null` si no hay API key
   *
   * @remarks
   * Para consumidores que no pueden cargar un style.json vectorial completo
   * (p.ej. el visor HTML plano de Bahía Blanca, servido dentro de un iframe
   * sin bundler) y solo necesitan un source `type: 'raster'` simple con
   * `tiles: [...]`. Reutiliza la misma key gestionada por este servicio;
   * nunca expone la key cruda ni agrega una segunda configuración.
   *
   * @see https://docs.maptiler.com/cloud/api/maps/ — Raster tiles (XYZ)
   */
  public getRasterTileUrl(type: MapStyleType): string | null {
    if (!this.hasApiKey()) return null;
    const metadata = MAP_STYLES_CONFIG[type];
    if (!metadata || type === 'solid') return null;
    return `https://api.maptiler.com/maps/${metadata.maptilerStyle}/256/{z}/{x}/{y}.png?key=${this.apiKey}`;
  }

  /**
   * Genera un estilo de MapLibre con color sólido
   *
   * @remarks
   * Crea un estilo inline minimalista con solo una capa de fondo del color configurado.
   * No requiere conexión a internet ni API keys.
   *
   * @returns Objeto de estilo MapLibre válido
   */
  private getSolidColorStyle(): object {
    const color = solidColorService.getColor();

    return {
      version: 8,
      name: 'WESTEER Solid Color',
      sources: {},
      layers: [
        {
          id: 'background',
          type: 'background',
          paint: {
            'background-color': color,
          },
        },
      ],
    };
  }

  /**
   * Obtiene metadata de un estilo específico
   *
   * @param type - Tipo de estilo
   * @returns Metadata del estilo
   */
  public getMetadata(type: MapStyleType): MapStyleMetadata {
    return getStyleMetadata(type);
  }

  /**
   * Obtiene todos los estilos disponibles
   *
   * @remarks
   * Si no hay API key, filtra estilos que no tienen fallback
   * para evitar mostrar opciones no funcionales.
   *
   * @returns Array de metadatas de estilos disponibles
   */
  public getAvailableStyles(): MapStyleMetadata[] {
    const allStyles = getAllStyles();

    // Si hay API key, todos los estilos están disponibles
    if (this.hasApiKey()) {
      return allStyles;
    }

    // Sin API key: solo estilos con fallback
    return allStyles.filter((style) => style.fallbackUrl !== null);
  }

  /**
   * Verifica si un estilo está disponible
   *
   * @param type - Tipo de estilo a verificar
   * @returns true si está disponible, false en caso contrario
   */
  public isStyleAvailable(type: MapStyleType): boolean {
    const metadata = MAP_STYLES_CONFIG[type];
    if (!metadata) return false;

    // Con API key, todos disponibles
    if (this.hasApiKey()) return true;

    // Sin API key, solo si tiene fallback
    return metadata.fallbackUrl !== null;
  }
}

/**
 * Instancia singleton exportada del servicio
 *
 * @example
 * ```typescript
 * import { mapStyleService } from '@/core/map-engine';
 *
 * const style = mapStyleService.getDefaultStyle();
 * const available = mapStyleService.getAvailableStyles();
 * ```
 */
export const mapStyleService = MapStyleService.getInstance();

// Re-exportar tipos para conveniencia
export type { MapStyleType, MapStyleMetadata } from './map-styles-config';
